const DELIMITER  = ";"
const UNDEF_BYTE = 0x5F // "_"

const WIN1252_SPECIALS = {
  0x20AC: 0x80, 0x201A: 0x82, 0x0192: 0x83, 0x201E: 0x84,
  0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87, 0x02C6: 0x88,
  0x2030: 0x89, 0x0160: 0x8A, 0x2039: 0x8B, 0x0152: 0x8C,
  0x017D: 0x8E, 0x2018: 0x91, 0x2019: 0x92, 0x201C: 0x93,
  0x201D: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02DC: 0x98, 0x2122: 0x99, 0x0161: 0x9A, 0x203A: 0x9B,
  0x0153: 0x9C, 0x017E: 0x9E, 0x0178: 0x9F
}

const ISO_8859_15_SPECIALS = {
  0x20AC: 0xA4, 0x0160: 0xA6, 0x0161: 0xA8, 0x017D: 0xB4,
  0x017E: 0xB8, 0x0152: 0xBC, 0x0153: 0xBD, 0x0178: 0xBE
}
const ISO_8859_15_REASSIGNED = new Set([0xA4, 0xA6, 0xA8, 0xB4, 0xB8, 0xBC, 0xBD, 0xBE])

const encodeUtf8 = str => new TextEncoder().encode(str)

const encodeAscii = str => {
  const bytes = []
  for (const ch of str) {
    const cp = ch.codePointAt(0)
    bytes.push(cp < 0x80 ? cp : UNDEF_BYTE)
  }
  return new Uint8Array(bytes)
}

const encodeLatin1 = str => {
  const bytes = []
  for (const ch of str) {
    const cp = ch.codePointAt(0)
    bytes.push(cp <= 0xFF ? cp : UNDEF_BYTE)
  }
  return new Uint8Array(bytes)
}

const encodeWindows1252 = str => {
  const bytes = []
  for (const ch of str) {
    const cp = ch.codePointAt(0)
    if (cp < 0x80 || (cp >= 0xA0 && cp <= 0xFF)) {
      bytes.push(cp)
    } else if (WIN1252_SPECIALS[cp] !== undefined) {
      bytes.push(WIN1252_SPECIALS[cp])
    } else {
      bytes.push(UNDEF_BYTE)
    }
  }
  return new Uint8Array(bytes)
}

const encodeIso885915 = str => {
  const bytes = []
  for (const ch of str) {
    const cp = ch.codePointAt(0)
    if (ISO_8859_15_SPECIALS[cp] !== undefined) {
      bytes.push(ISO_8859_15_SPECIALS[cp])
    } else if (cp <= 0xFF && !ISO_8859_15_REASSIGNED.has(cp)) {
      bytes.push(cp)
    } else {
      bytes.push(UNDEF_BYTE)
    }
  }
  return new Uint8Array(bytes)
}

const ENCODERS = {
  'UTF8':       encodeUtf8,
  'USASCII':    encodeAscii,
  'ASCII':      encodeAscii,
  'WINDOWS1252': encodeWindows1252,
  'CP1252':     encodeWindows1252,
  'ISO88591':   encodeLatin1,
  'LATIN1':     encodeLatin1,
  'ISO885915':  encodeIso885915,
  'LATIN9':     encodeIso885915
}

const pickEncoder = name => {
  const key = String(name || 'UTF-8').toUpperCase().replace(/[-_]/g, '')
  const encoder = ENCODERS[key]
  if (encoder) return encoder
  // eslint-disable-next-line no-console
  console.warn(`[sales_statistics] unsupported CSV encoding "${name}", falling back to UTF-8`)
  return encodeUtf8
}

const formatAmount = value => {
  const number = Number(value) || 0
  return number.toLocaleString(I18n.locale, {
    useGrouping:           false,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const escapeField = field => {
  const str = String(field ?? "")
  if (str.includes(DELIMITER) || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

const buildRow = fields => fields.map(escapeField).join(DELIMITER)

export const buildSalesCsv = ({ data, months, currency, typeLabel }) => {
  const monthlyType = data?.props_month_type
  if (!monthlyType) return ""

  const totalLabel  = I18n.t("invoices.sales_statistics.total")
  const headerLabel = currency ? `${totalLabel} (${currency})` : totalLabel

  const rows = []
  rows.push(buildRow([typeLabel, headerLabel, ...months]))

  const monthKeys = Array.from({ length: months.length }, (_, i) => i + 1)

  rows.push(buildRow([
    "",
    formatAmount(monthlyType.totals?.total),
    ...monthKeys.map(k => formatAmount(monthlyType.totals?.[k]))
  ]))

  Object.entries(monthlyType.members || {}).forEach(([name, values]) => {
    rows.push(buildRow([
      name,
      formatAmount(values.total),
      ...monthKeys.map(k => formatAmount(values[k]))
    ]))
  })

  return rows.join("\r\n")
}

export const downloadSalesCsv = ({ data, months, currency, typeLabel, filename, encodingName }) => {
  const csv = buildSalesCsv({ data, months, currency, typeLabel })
  if (!csv) return

  const bytes = pickEncoder(encodingName)(csv)
  const blob  = new Blob([bytes], { type: "text/csv" })
  const url   = URL.createObjectURL(blob)
  const link  = document.createElement("a")
  link.href     = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
